import React from "react";
import { useMediaQuery } from "react-responsive";
import verysad from "assets/images/verysad.svg";
import sad from "assets/images/sad.svg";
import netural from "assets/images/netural.svg";
import happy from "assets/images/happy.svg";
import veryhappy from "assets/images/veryhappy.svg";
import appstore from "assets/images/appstore.svg";
import googleplay from "assets/images/googleplay.svg";
import ReceiptPageWrapper from "./receiptPage.styles";
import ReceiptEmailCapture from "./ReceiptEmailCapture";
import ReceiptPromoSection from "./ReceiptPromoSection";
import {
  linkFieldByPlatform,
  parseSocialConfig,
  socialPlatformMeta
} from "util/receiptPageDefaults";

const emoji = [
  { image: verysad, value: 1 },
  { image: sad, value: 2 },
  { image: netural, value: 3 },
  { image: happy, value: 4 },
  { image: veryhappy, value: 5 }
];

const openLink = url => {
  if (url) window.open(url, "_blank", "noopener,noreferrer");
};

const ReceiptIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M6 2h12a2 2 0 0 1 2 2v16l-4-3-4 3-4-3-4 3V4a2 2 0 0 1 2-2z"
      fill="none"
      strokeWidth="1.8"
    />
    <path
      d="M8 7h8M8 11h8"
      fill="none"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const ExternalLinkIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M14 5h5v5" fill="none" strokeWidth="1.8" strokeLinecap="round" />
    <path d="M10 14 19 5" fill="none" strokeWidth="1.8" strokeLinecap="round" />
    <path
      d="M19 14v5H5V5h5"
      fill="none"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const WalletIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M4 7h14a3 3 0 0 1 3 3v7H7a3 3 0 0 1-3-3V7z"
      fill="none"
      strokeWidth="1.8"
    />
    <path
      d="M7 7V5a2 2 0 0 1 2-2h9"
      fill="none"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <circle cx="17.5" cy="13.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const StarIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="m12 4 2.4 4.9 5.4.8-3.9 3.8.9 5.4L12 16.8 7.2 18.9l.9-5.4L4.2 9.7l5.4-.8L12 4z"
      fill="none"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const PdfIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M8 3h8l4 4v14H8V3z" fill="none" strokeWidth="1.8" />
    <path d="M16 3v4h4" fill="none" strokeWidth="1.8" />
    <path
      d="M10 13h4M10 17h2"
      fill="none"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

export default function ReceiptPageView({
  receiptData,
  loading = false,
  previewMode = false,
  embedded = false,
  receiptName,
  onEmailSubmitted,
  onDownload,
  onWallet,
  walletLoading = false,
  onRating
}) {
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const socialConfig = parseSocialConfig(receiptData?.socialConfig);
  const primary = receiptData?.primaryColor || "#1F3B6F";
  const secondary = receiptData?.secondaryColor || "#4CAF50";
  const isPdf =
    receiptData?.receiptPath
      ?.split(".")
      .pop()
      ?.toLowerCase() === "pdf";

  const visibleSocial = socialPlatformMeta.filter(platform => {
    if (platform.key === "trustpilot") return false;
    const field = linkFieldByPlatform[platform.key];
    const link = receiptData?.[field];
    return socialConfig[platform.key] !== false && link;
  });

  const showApps =
    (socialConfig.appStore !== false && receiptData?.appStoreLink) ||
    (socialConfig.playStore !== false && receiptData?.playStoreLink);

  const storeInitial =
    receiptData?.storeName
      ?.trim()
      ?.charAt(0)
      ?.toUpperCase() || "G";

  if (loading) {
    return (
      <ReceiptPageWrapper
        $primary={primary}
        $secondary={secondary}
        $embedded={embedded}
      >
        <div className="rp-loading">Loading your receipt…</div>
      </ReceiptPageWrapper>
    );
  }

  return (
    <ReceiptPageWrapper
      $primary={primary}
      $secondary={secondary}
      $embedded={embedded}
    >
      {previewMode && (
        <div className="rp-preview-banner">Preview — sample receipt layout</div>
      )}

      <div className="rp-shell">
        <header className="rp-header">
          {receiptData?.storeLogo ? (
            <img
              src={receiptData.storeLogo}
              alt={receiptData.storeName || "Store"}
              className="rp-logo"
            />
          ) : (
            <div className="rp-logo-fallback">{storeInitial}</div>
          )}
          <h1 className="rp-store-name">
            {receiptData?.storeName || "Green Till"}
          </h1>
          {receiptData?.receiptTagline && (
            <p className="rp-tagline">{receiptData.receiptTagline}</p>
          )}
        </header>

        <section className="rp-card">
          <div className="rp-receipt-frame">
            {!receiptData?.receiptPath ? (
              <div className="rp-empty-receipt">Receipt will appear here</div>
            ) : isPdf && isMobile && !previewMode ? (
              <div className="rp-pdf-mobile">
                <div className="rp-pdf-icon" aria-hidden="true">
                  <PdfIcon />
                </div>
                <h2 className="rp-pdf-title">Your receipt is ready</h2>
                <p>Tap the button below to open and view your full receipt.</p>
                <a
                  className="rp-view-receipt-btn"
                  href={receiptData.receiptPath}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <span className="rp-btn-icon">
                    <ReceiptIcon />
                  </span>
                  <span className="rp-btn-label">View receipt</span>
                  <span className="rp-btn-icon">
                    <ExternalLinkIcon />
                  </span>
                </a>
              </div>
            ) : isPdf ? (
              <iframe
                className="rp-receipt-pdf"
                title="Receipt PDF"
                src={receiptData.receiptPath}
              />
            ) : (
              <img
                src={receiptData.receiptPath}
                alt="Receipt"
                className="rp-receipt-img"
              />
            )}
          </div>

          {(receiptName || previewMode) && (
            <ReceiptEmailCapture
              receiptName={receiptName || "preview"}
              previewMode={previewMode}
              onEmailSubmitted={onEmailSubmitted}
              downloadUrl={receiptData?.downloadUrl}
              onDownload={onDownload}
              consentMessage={receiptData?.consentMessage}
              receiptPrivacyText={receiptData?.receiptPrivacyText}
              receiptPrivacyPolicy={receiptData?.receiptPrivacyPolicy}
              primaryColor={primary}
              secondaryColor={secondary}
            />
          )}

          {((socialConfig.wallet !== false && !previewMode) ||
            (socialConfig.trustpilot !== false &&
              receiptData?.trustpilotLink)) && (
            <div className="rp-actions">
              {socialConfig.wallet !== false && !previewMode && (
                <button
                  type="button"
                  className="rp-btn rp-btn-secondary"
                  onClick={onWallet}
                  disabled={walletLoading}
                >
                  <span className="rp-btn-icon">
                    <WalletIcon />
                  </span>
                  <span className="rp-btn-label">
                    {walletLoading ? "Adding…" : "Add to Google Wallet"}
                  </span>
                </button>
              )}
              {socialConfig.trustpilot !== false &&
                receiptData?.trustpilotLink && (
                  <button
                    type="button"
                    className="rp-btn rp-btn-trustpilot"
                    onClick={() => openLink(receiptData.trustpilotLink)}
                  >
                    <span className="rp-btn-icon">
                      <StarIcon />
                    </span>
                    <span className="rp-btn-label">
                      Review us on Trustpilot
                    </span>
                  </button>
                )}
            </div>
          )}
        </section>

        {visibleSocial.length > 0 && (
          <section className="rp-card">
            <h2 className="rp-section-title">Connect with us</h2>
            <div className="rp-social-grid">
              {visibleSocial.map(platform => (
                <button
                  key={platform.key}
                  type="button"
                  className="rp-social-btn"
                  style={{ background: platform.color }}
                  aria-label={platform.label}
                  onClick={() =>
                    openLink(receiptData[linkFieldByPlatform[platform.key]])
                  }
                >
                  <i className={platform.icon} />
                </button>
              ))}
            </div>
          </section>
        )}

        <ReceiptPromoSection
          ads={receiptData?.promotionalAdvertisementList || []}
        />

        {socialConfig.feedback !== false && (
          <section className="rp-card rp-feedback">
            <h2 className="rp-section-title">How was your experience?</h2>
            <div className="rp-emoji-row">
              {emoji.map(item => (
                <button
                  key={item.value}
                  type="button"
                  className="rp-emoji-btn"
                  onClick={() => onRating && onRating(item.value)}
                  disabled={previewMode}
                >
                  <img src={item.image} alt={`Rating ${item.value}`} />
                </button>
              ))}
            </div>
          </section>
        )}

        {showApps && (
          <section className="rp-card">
            <h2 className="rp-section-title">Get the app</h2>
            <div className="rp-app-badges">
              {socialConfig.appStore !== false && receiptData?.appStoreLink && (
                <img
                  src={appstore}
                  alt="App Store"
                  onClick={() => openLink(receiptData.appStoreLink)}
                />
              )}
              {socialConfig.playStore !== false &&
                receiptData?.playStoreLink && (
                  <img
                    src={googleplay}
                    alt="Google Play"
                    onClick={() => openLink(receiptData.playStoreLink)}
                  />
                )}
            </div>
          </section>
        )}
      </div>
    </ReceiptPageWrapper>
  );
}
